import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Store,
  BarChart3,
  Headset,
  MapPin,
  Globe2,
  Settings2,
  Users,
  Star,
  Circle,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Reveal, Chapter } from "@/components/Shared";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1400&q=80";
const COLUMN_IMAGES = [
  "https://images.unsplash.com/photo-1556740738-b6a63e27c4df?auto=format&fit=crop&w=600&q=80",
  "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=600&q=80",
  "https://images.unsplash.com/photo-1587560699334-cc4ff634909a?auto=format&fit=crop&w=600&q=80",
];
const CLIENT_IMAGES = [
  "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1481437156560-3205f6a55735?auto=format&fit=crop&w=900&q=80",
];
const CLIENT_ICONS = [Store, Store, Store, Globe2];
const PORTRAIT =
  "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=200&q=80";

const THEMES = [
  {
    tag: "text-emerald-600",
    icon: "bg-emerald-100 text-emerald-600",
    check: "text-emerald-500",
    chip: "bg-emerald-500",
    soft: "bg-emerald-50/70 border-emerald-100",
  },
  {
    tag: "text-blue-600",
    icon: "bg-blue-100 text-blue-600",
    check: "text-blue-500",
    chip: "bg-blue-600",
    soft: "bg-blue-50/70 border-blue-100",
  },
  {
    tag: "text-violet-600",
    icon: "bg-violet-100 text-violet-600",
    check: "text-violet-500",
    chip: "bg-violet-500",
    soft: "bg-violet-50/70 border-violet-100",
  },
];
const COLUMN_ICONS = [Store, BarChart3, Headset];
const STAT_ICONS = [Store, Globe2, Settings2, Users];

const PhoneMock = () => {
  const tasks = [
    { label: "Ouverture magasin", time: "07:00", done: true },
    { label: "Contrôle fraîcheur", time: "08:30", done: true },
    { label: "Inventaire produits", time: "10:00", done: true },
    { label: "Mise en place opération", time: "11:30", done: true },
    { label: "Relevé températures", time: "14:00", done: false },
    { label: "Clôture journée", time: "18:00", done: false },
  ];
  return (
    <div
      data-testid="mock-phone"
      className="mx-auto w-44 rounded-[1.8rem] border-[6px] border-slate-900 bg-white p-3 shadow-xl"
    >
      <p className="text-center font-display text-[11px] font-bold text-slate-900">Ma journée</p>
      <p className="text-center text-[8px] text-slate-400">Mardi 8 septembre</p>
      <div className="relative mx-auto my-2.5 h-16 w-16">
        <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
          <circle cx="32" cy="32" r="28" fill="none" stroke="#E2E8F0" strokeWidth="7" />
          <circle
            cx="32"
            cy="32"
            r="28"
            fill="none"
            stroke="#10B981"
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={`${((2 * Math.PI * 28) / 6) * 4} ${2 * Math.PI * 28}`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-mono-orbit text-xs font-bold text-slate-900">4/6</span>
          <span className="text-[6px] text-slate-400">tâches</span>
        </div>
      </div>
      {tasks.map((task) => (
        <div key={task.label} className="flex items-center gap-1.5 border-t border-slate-50 py-1.5">
          {task.done ? (
            <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-500" />
          ) : (
            <Circle className="h-3 w-3 shrink-0 text-slate-300" />
          )}
          <span className={`flex-1 truncate text-[8px] font-medium ${task.done ? "text-slate-400" : "text-slate-700"}`}>
            {task.label}
          </span>
          <span className="font-mono-orbit text-[7px] text-slate-400">{task.time}</span>
        </div>
      ))}
    </div>
  );
};

const ManagerMock = () => {
  const ranking = [
    { name: "Lille Centre", score: 98 },
    { name: "Roubaix", score: 95 },
    { name: "Villeneuve-d'Ascq", score: 92 },
    { name: "Tourcoing", score: 78 },
    { name: "Lens", score: 76 },
  ];
  return (
    <div data-testid="mock-manager" className="rounded-2xl border border-slate-100 bg-white p-4 shadow-lg">
      <p className="flex items-center gap-1.5 text-[10px] font-bold text-slate-900">
        <MapPin className="h-3 w-3 text-blue-600" /> Lille & Nord (12 magasins)
      </p>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {[
          { label: "CA semaine", value: "124 650 €", trend: "+8%" },
          { label: "Taux d'atteinte", value: "92%", trend: "+4 pts" },
          { label: "En alerte", value: "3", trend: "prioritaires" },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-lg bg-slate-50 p-2">
            <p className="text-[7px] font-semibold uppercase tracking-wide text-slate-400">{kpi.label}</p>
            <p className="font-mono-orbit text-[11px] font-bold text-slate-900">{kpi.value}</p>
            <p className="text-[7px] font-semibold text-emerald-500">{kpi.trend}</p>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[9px] font-bold text-slate-700">Classement magasins</p>
      {ranking.map((store, i) => (
        <div key={store.name} className="mt-1.5 flex items-center gap-2">
          <span className="font-mono-orbit w-3 text-[8px] text-slate-400">{i + 1}</span>
          <span className="w-24 truncate text-[9px] font-medium text-slate-600">{store.name}</span>
          <div className="h-1.5 flex-1 rounded-full bg-slate-100">
            <div
              className={`h-1.5 rounded-full ${store.score >= 90 ? "bg-emerald-500" : "bg-amber-400"}`}
              style={{ width: `${store.score}%` }}
            />
          </div>
          <span className="font-mono-orbit text-[8px] font-bold text-slate-700">{store.score}%</span>
        </div>
      ))}
    </div>
  );
};

const SupportMock = () => {
  const rows = [
    { id: "#4581", subject: "Panne chambre froide", store: "Lille Centre", status: "En cours", sColor: "bg-blue-50 text-blue-600", priority: "Haute", pColor: "bg-rose-50 text-rose-600" },
    { id: "#4579", subject: "Manque de stock", store: "Marseille Prado", status: "En cours", sColor: "bg-blue-50 text-blue-600", priority: "Moyenne", pColor: "bg-amber-50 text-amber-600" },
    { id: "#4576", subject: "Demande de formation", store: "Lyon Part-Dieu", status: "Résolue", sColor: "bg-emerald-50 text-emerald-600", priority: "Basse", pColor: "bg-slate-100 text-slate-500" },
    { id: "#4572", subject: "Problème climatisation", store: "Bordeaux Lac", status: "En cours", sColor: "bg-blue-50 text-blue-600", priority: "Haute", pColor: "bg-rose-50 text-rose-600" },
  ];
  return (
    <div data-testid="mock-support" className="rounded-2xl border border-slate-100 bg-white p-4 shadow-lg">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold text-slate-900">Demandes & incidents</p>
        <span className="rounded-md bg-blue-600 px-2 py-1 text-[8px] font-bold text-white">+ Nouvelle demande</span>
      </div>
      <p className="mt-1.5 text-[8px] text-slate-400">Toutes (24) · En cours (8) · En attente (4) · Résolues (12)</p>
      <div className="mt-2 space-y-1.5">
        {rows.map((row) => (
          <div key={row.id} className="flex items-center gap-2 rounded-lg border border-slate-50 px-2 py-1.5">
            <span className="font-mono-orbit text-[8px] text-slate-400">{row.id}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[9px] font-semibold text-slate-800">{row.subject}</p>
              <p className="truncate text-[7px] text-slate-400">{row.store}</p>
            </div>
            <span className={`rounded-full px-1.5 py-0.5 text-[7px] font-bold ${row.sColor}`}>{row.status}</span>
            <span className={`rounded-full px-1.5 py-0.5 text-[7px] font-bold ${row.pColor}`}>{row.priority}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const MOCKS = [PhoneMock, ManagerMock, SupportMock];

const WhoPage = () => {
  const { t } = useLanguage();
  usePageTitle(t.nav.who);
  const w = t.who;

  return (
    <main data-testid="who-page" className="pt-[76px]">
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50/80 via-white to-white py-16 lg:py-24">
        <div className="pointer-events-none absolute inset-0 bg-grid-light opacity-60 [mask-image:radial-gradient(ellipse_at_top,black_20%,transparent_70%)]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2 lg:px-8">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid="who-eyebrow"
              className="inline-block max-w-md text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-500 sm:text-xs"
            >
              {w.eyebrow}
            </motion.p>
            <h1
              data-testid="who-title"
              className="mt-5 font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
            >
              {w.titleLines.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${i >= w.blueFrom ? "text-blue-600" : ""}`}
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid="who-subtitle"
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              {w.subtitle}
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
            data-testid="who-hero-image"
          >
            <p className="font-hand absolute -top-10 right-2 z-10 rotate-[-3deg] text-xl font-semibold text-amber-500 lg:text-2xl">
              {w.annotation}
            </p>
            <img
              src={HERO_IMAGE}
              alt="Équipe Orbit"
              className="h-72 w-full rounded-2xl object-cover shadow-[0_32px_64px_-20px_rgba(10,15,29,0.3)] lg:h-96"
            />
            <div className="absolute inset-x-4 bottom-4 flex flex-wrap justify-center gap-2.5 lg:gap-3">
              {w.heroBadges.map((badge, i) => {
                const Icon = COLUMN_ICONS[i];
                const theme = THEMES[i];
                return (
                  <motion.span
                    key={badge.tag}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.9 + i * 0.15 }}
                    className="flex items-center gap-2 rounded-xl bg-white/95 px-3.5 py-2.5 shadow-lg backdrop-blur"
                  >
                    <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${theme.icon}`}>
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span>
                      <span className={`block text-[9px] font-extrabold uppercase tracking-wider ${theme.tag}`}>
                        {badge.tag}
                      </span>
                      <span className="block text-[11px] font-bold text-slate-900">{badge.label}</span>
                    </span>
                  </motion.span>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="who-users-section" className="py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Chapter index={w.users.chapter} label={t.nav.who} />
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              {w.users.title}
            </h2>
            <p className="mt-4 max-w-xl text-base text-slate-600 sm:text-lg">{w.users.subtitle}</p>
          </Reveal>
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {w.users.columns.map((col, i) => {
              const theme = THEMES[i];
              const Icon = COLUMN_ICONS[i];
              const Mock = MOCKS[i];
              return (
                <Reveal key={col.tag} delay={i * 0.12} className="h-full">
                  <div
                    data-testid={`who-column-${i}`}
                    className={`flex h-full flex-col rounded-2xl border p-7 ${theme.soft}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${theme.icon}`}>
                        <Icon className="h-5 w-5" />
                      </span>
                      <div>
                        <p className={`text-[10px] font-extrabold uppercase tracking-[0.16em] ${theme.tag}`}>
                          {col.tag}
                        </p>
                        <p className="text-xs font-semibold text-slate-500">{col.role}</p>
                      </div>
                    </div>
                    <div className="mt-5 flex items-start gap-4">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-display text-lg font-bold text-slate-900">{col.title}</h3>
                        <p className="mt-2 text-[13px] leading-relaxed text-slate-600">{col.text}</p>
                      </div>
                      <img
                        src={COLUMN_IMAGES[i]}
                        alt={col.tag}
                        loading="lazy"
                        className="h-24 w-20 shrink-0 rounded-xl object-cover"
                      />
                    </div>
                    <ul className="mt-5 space-y-2.5">
                      {col.points.map((point) => (
                        <li key={point} className="flex items-start gap-2.5 text-sm font-medium text-slate-700">
                          <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${theme.check}`} />
                          {point}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-5 border-t border-slate-900/5 pt-4 text-[13px] italic leading-snug text-slate-600">
                      “{col.quote}”
                    </p>
                    <div className="mt-6 flex-1">
                      <Mock />
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-16 flex flex-col items-center gap-6 lg:flex-row lg:items-stretch lg:justify-center lg:gap-0">
              {w.users.flow.map((step, i) => {
                const theme = THEMES[i];
                const Icon = COLUMN_ICONS[i];
                return (
                  <div key={step.label} className="flex flex-col items-center gap-6 lg:flex-row lg:gap-0">
                    <div
                      data-testid={`who-flow-step-${i}`}
                      className="flex w-64 flex-col items-center rounded-2xl border border-slate-100 bg-white p-6 text-center shadow-[0_16px_40px_-20px_rgba(10,15,29,0.15)]"
                    >
                      <span className={`flex h-11 w-11 items-center justify-center rounded-full ${theme.icon}`}>
                        <Icon className="h-5 w-5" />
                      </span>
                      <p className="mt-3 font-display text-sm font-extrabold uppercase tracking-wider text-slate-900">
                        {step.label}
                      </p>
                      <p className="mt-1 text-xs font-semibold text-slate-500">{step.actions}</p>
                    </div>
                    {i < w.users.flow.length - 1 && (
                      <div className="flex items-center lg:mx-4">
                        <ArrowRight className="h-5 w-5 rotate-90 text-slate-300 lg:rotate-0" />
                        <img src="/assets/orbit-logo.png" alt="Orbit" className="mx-2 hidden h-6 w-auto lg:block" />
                        <ArrowRight className="hidden h-5 w-5 text-slate-300 lg:block" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="who-clients-section" className="bg-slate-50/70 py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="relative">
            <Chapter index={w.clients.chapter} label={t.chapters.who} />
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              {w.clients.title}
            </h2>
            <p className="mt-4 max-w-xl text-base text-slate-600 sm:text-lg">{w.clients.subtitle}</p>
            <p
              data-testid="who-clients-annotation"
              className="font-hand mt-4 max-w-xs rotate-[-2deg] text-xl font-semibold text-amber-500 lg:absolute lg:right-4 lg:top-2 lg:mt-0 lg:text-2xl"
            >
              {w.clients.annotation}
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
            {w.clients.cards.map((card, i) => (
              <Reveal key={card.slug} delay={i * 0.1} className="h-full">
                <div
                  data-testid={`who-client-card-${i}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-16px_rgba(10,15,29,0.14)]"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={CLIENT_IMAGES[i]}
                      alt={card.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute bottom-3 left-3 flex h-9 w-9 items-center justify-center rounded-lg bg-white/95 shadow">
                      {(() => {
                        const Icon = CLIENT_ICONS[i];
                        return <Icon className="h-4 w-4 text-blue-600" />;
                      })()}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-base font-bold leading-snug text-slate-900">{card.title}</h3>
                    <p className="mt-2 flex-1 text-[13px] leading-relaxed text-slate-600">{card.text}</p>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {card.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold text-blue-600"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <Link
                      to={`/secteurs/${card.slug}`}
                      data-testid={`who-client-more-${i}`}
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 transition-colors hover:text-blue-700"
                    >
                      {w.clients.more}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="who-stats-section" className="relative overflow-hidden bg-[#0A0F1D] py-16 lg:py-24">
        <div className="pointer-events-none absolute inset-0 bg-grid-navy" />
        <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-blue-600/15 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 lg:grid-cols-[1fr_2.2fr] lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
              {w.stats.title}
              <span className="block text-emerald-400">{w.stats.title2}</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 gap-x-8 gap-y-8 lg:grid-cols-4">
            {w.stats.items.map((item, i) => {
              const Icon = STAT_ICONS[i];
              return (
                <Reveal key={item} delay={i * 0.1}>
                  <div data-testid={`who-stat-${i}`} className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/5 ring-1 ring-white/10">
                      <Icon className="h-5 w-5 text-emerald-400" />
                    </span>
                    <p className="text-sm font-bold leading-snug text-white">{item}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="who-testimonial-section" className="py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="flex h-full flex-col justify-between rounded-2xl border border-slate-100 bg-white p-9 shadow-[0_16px_40px_-16px_rgba(10,15,29,0.08)]">
              <div>
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <blockquote className="mt-6 font-display text-xl font-medium leading-relaxed text-slate-800 text-balance">
                  “{w.testimonial.quote}”
                </blockquote>
              </div>
              <div className="mt-8 flex items-center gap-4">
                <img
                  src={PORTRAIT}
                  alt={w.testimonial.name}
                  loading="lazy"
                  className="h-12 w-12 rounded-full object-cover ring-2 ring-blue-100"
                />
                <div>
                  <p className="text-sm font-bold text-slate-900">{w.testimonial.name}</p>
                  <p className="text-xs text-slate-500">{w.testimonial.role}</p>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="relative flex h-full flex-col justify-center overflow-hidden rounded-2xl bg-[#0A0F1D] p-9 lg:p-12">
              <div className="pointer-events-none absolute inset-0 bg-grid-navy" />
              <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-emerald-500/15 blur-3xl" />
              <div className="relative">
                <h3 className="font-display text-2xl font-bold tracking-tight text-white lg:text-3xl text-balance">
                  {w.testimonial.ctaTitle}
                </h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400">{w.testimonial.ctaText}</p>
                <Link
                  to="/demo"
                  data-testid="who-cta-button"
                  className="group mt-7 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-blue-500"
                >
                  {w.testimonial.ctaButton}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default WhoPage;
